'use client';


import { useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import { Loader2 } from 'lucide-react';
import { calculateRoomModes } from '@/lib/acousticsPhysics';
import {
  absPressureAt,
  computeModalSeatResponse,
  findNearestMode,
  isPressureNull,
  pressureFieldFloor,
} from '@/lib/tools/modalPressure';
import { clamp } from '@/lib/tools/roomLayout';
import { toolFreqGrid } from '@/lib/tools/sbir';
import { buildNamedRoomStates, flatMagnitude } from '@/lib/tools/syntheticAcoustics';
import type { LengthUnit } from '@/lib/tools/units';
import { CONTENT, CONTENT_NAME, SECOND_LINE } from '@/components/seo/roles';
import Slider, { SliderValueField } from '@/components/ui/Slider';
import ToolFreqChart from '@/components/tools/ToolFreqChart';
import { ToolMultiAuralization } from '@/components/tools/ToolAuralization';
import ToolLeadIn from '@/components/tools/ToolLeadIn';
import UnitToggle from '@/components/tools/UnitToggle';
import ToolDimSlider from '@/components/tools/ToolDimSlider';
import { useInViewOnce } from '@/lib/useInViewOnce';
import { HEATMAP_STAGE_CLASS } from './heatmapStage';

function parseHz(raw: string): number | null {
  const n = parseFloat(raw.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

/** The poster the 3D map stands on until it loads: the canvas's own box, so
 *  the swap shifts nothing (CLS). Keeps `mode-heatmap-3d` off it, so a test
 *  waiting on that id waits for the real canvas. A visible spinner + label
 *  (#13, cohesion audit) — this used to be a silent blank box, which the
 *  onboarding "touch-action: none on a canvas" gotcha already taught reads as
 *  a broken control, not a loading one. Still `aria-hidden`: the real canvas
 *  carries its own label once it lands, and this is a transient state with no
 *  live region to announce it into. */
function HeatmapPoster() {
  return (
    <div className={`${HEATMAP_STAGE_CLASS} flex items-center justify-center gap-2`} data-testid="mode-heatmap-poster" aria-hidden>
      <Loader2 className="h-4 w-4 shrink-0 animate-spin text-[var(--rt-ink-dim)]" />
      <span className={SECOND_LINE}>Loading 3D map…</span>
    </div>
  );
}

// three.js + R3F are most of this page's JavaScript and the map sits below the
// controls, so the chunk is fetched only once the map nears the viewport (S6).
// This once had to be a static import because next/dynamic left an e2e stuck
// on its loading text; the e2e now scrolls the section into view and waits on
// `mode-heatmap-3d`, which only the real canvas carries.
const ModeHeatmapCanvas = dynamic(() => import('./ModeHeatmapCanvas'), {
  ssr: false,
  loading: HeatmapPoster,
});

const MARGIN = 0.3;
const EAR_Z = 1.2;

export default function RoomModeExplorerWidget() {
  const [unit, setUnit] = useState<LengthUnit>('m');
  const [lengthM, setLengthM] = useState(5);
  const [widthM, setWidthM] = useState(4);
  const [heightM, setHeightM] = useState(2.6);
  const [targetHz, setTargetHz] = useState(60);
  const [speakerXM, setSpeakerXM] = useState(1.0);
  const [speakerYM, setSpeakerYM] = useState(1.0);
  // Avoid room-center defaults — many low modes have a null there (e2e + UX).
  const [deskXM, setDeskXM] = useState(1.9);
  const [deskYM, setDeskYM] = useState(1.4);
  const [heatmapRef, heatmapNear] = useInViewOnce<HTMLDivElement>();

  const applyRoom = (nextL: number, nextW: number, nextH = heightM) => {
    const L = clamp(nextL, 3, 10);
    const W = clamp(nextW, 2.5, 8);
    const H = clamp(nextH, 2, 4);
    setLengthM(L);
    setWidthM(W);
    setHeightM(H);
    setSpeakerXM((x) => clamp(x, MARGIN, L - MARGIN));
    setSpeakerYM((y) => clamp(y, MARGIN, W - MARGIN));
    setDeskXM((x) => clamp(x, MARGIN, L - MARGIN));
    setDeskYM((y) => clamp(y, MARGIN, W - MARGIN));
  };

  const room = useMemo(() => ({ lengthM, widthM, heightM }), [lengthM, widthM, heightM]);
  const modes = useMemo(
    () => calculateRoomModes(lengthM, widthM, heightM, 250),
    [lengthM, widthM, heightM],
  );
  const mode = useMemo(() => findNearestMode(modes, targetHz), [modes, targetHz]);
  const grid = useMemo(
    () => (mode ? pressureFieldFloor(mode, room, 40, 28, EAR_Z) : null),
    [mode, room],
  );
  const deskPressure = useMemo(
    () => (mode ? absPressureAt(mode, room, deskXM, deskYM, EAR_Z) : 1),
    [mode, room, deskXM, deskYM],
  );
  const deskIsNull = isPressureNull(deskPressure);

  const freqs = useMemo(() => toolFreqGrid(20, 250, 140), []);
  const seat = useMemo(
    () =>
      computeModalSeatResponse(
        freqs,
        room,
        { x: speakerXM, y: speakerYM, z: EAR_Z },
        { x: deskXM, y: deskYM, z: EAR_Z },
      ),
    [freqs, room, speakerXM, speakerYM, deskXM, deskYM],
  );
  const magPure = useMemo(() => flatMagnitude(freqs), [freqs]);
  const states = useMemo(
    () =>
      buildNamedRoomStates(freqs, [
        { id: 'pure', mag: magPure, nProducts: 0, rt60FineSec: 0.22, dryBroadband: true },
        { id: 'seat', mag: [...seat.magAudio], nProducts: 0, rt60FineSec: 0.55, dryBroadband: false },
      ]),
    [freqs, magPure, seat],
  );

  const modeTicks = useMemo(
    () => modes.map((m) => ({ hz: m.freqHz, emphasis: m.type })),
    [modes],
  );

  const result =
    deskIsNull && mode
      ? `Warning: your desk sits in a ${mode.freqHz.toFixed(0)} Hz pressure null — bass will sound thin here.`
      : mode
        ? `Heatmap: mode ${mode.label} at ${mode.freqHz.toFixed(1)} Hz. Seat curve uses all modes ≤250 Hz.`
        : 'Adjust the room to explore standing-wave pressure.';

  return (
    <div className="space-y-8">
      <div className="flex justify-end">
        <UnitToggle unit={unit} onChange={setUnit} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <ToolDimSlider
          label="Length"
          valueM={lengthM}
          minM={3}
          maxM={10}
          unit={unit}
          onChangeM={(v) => applyRoom(v, widthM)}
          testId="dim-length"
        />
        <ToolDimSlider
          label="Width"
          valueM={widthM}
          minM={2.5}
          maxM={8}
          unit={unit}
          onChangeM={(v) => applyRoom(lengthM, v)}
          testId="dim-width"
        />
        <ToolDimSlider
          label="Height"
          valueM={heightM}
          minM={2}
          maxM={4}
          unit={unit}
          onChangeM={(v) => applyRoom(lengthM, widthM, v)}
          testId="dim-height"
        />
        <div className="space-y-1.5">
          {/* Every other dimension here is a ToolDimSlider; this one used to be
              a bare native <input type="range"> — no click-to-edit readout, a
              visibly thicker track — the exact hand-rolled control the Slider
              rule exists to prevent (S6, cohesion audit). Frequency isn't a
              length, so it takes the primitive directly instead of going
              through ToolDimSlider's metres conversion. */}
          <Slider
            label="Heatmap mode"
            min={30}
            max={200}
            step={1}
            color="var(--rt-accent)"
            thumbs={[
              {
                value: targetHz,
                onChange: setTargetHz,
                ariaLabel: 'Heatmap mode frequency',
                testId: 'heatmap-mode-thumb',
                format: (v) => v.toFixed(0),
                parse: parseHz,
              },
            ]}
            renderReadout={(thumbs) => {
              const t = thumbs[0]!;
              return (
                <span className="inline-flex items-center gap-1 font-mono text-[var(--rt-accent)] font-bold">
                  <SliderValueField
                    value={t.value}
                    onCommit={(v) => setTargetHz(clamp(v, 30, 200))}
                    format={t.format}
                    parse={t.parse}
                    min={30}
                    max={200}
                    ariaLabel={t.ariaLabel}
                    testId="heatmap-mode"
                    className="w-[4.5rem] bg-transparent text-right font-mono text-caption font-bold text-[var(--rt-accent)] outline-none border-b border-transparent focus:border-[color-mix(in_oklch,var(--rt-accent)_60%,transparent)]"
                  />
                  <span className="text-[var(--rt-accent)]">Hz</span>
                </span>
              );
            }}
          />
          <p className={SECOND_LINE}>Nearest resonance · map only</p>
        </div>
        <ToolDimSlider
          label="Speaker X (length)"
          valueM={speakerXM}
          minM={MARGIN}
          maxM={lengthM - MARGIN}
          unit={unit}
          onChangeM={setSpeakerXM}
          testId="dim-spk-x"
        />
        <ToolDimSlider
          label="Speaker Y (width)"
          valueM={speakerYM}
          minM={MARGIN}
          maxM={widthM - MARGIN}
          unit={unit}
          onChangeM={setSpeakerYM}
          testId="dim-spk-y"
        />
        <ToolDimSlider
          label="Desk X (length)"
          valueM={deskXM}
          minM={MARGIN}
          maxM={lengthM - MARGIN}
          unit={unit}
          onChangeM={setDeskXM}
          testId="dim-desk-x"
        />
        <ToolDimSlider
          label="Desk Y (width)"
          valueM={deskYM}
          minM={MARGIN}
          maxM={widthM - MARGIN}
          unit={unit}
          onChangeM={setDeskYM}
          testId="dim-desk-y"
        />
      </div>

      {mode && grid && (
        <div className="space-y-2" data-testid="mode-heatmap-section">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div>
              <h3 className={CONTENT_NAME}>Single-mode pressure map</h3>
              <p className={`mt-0.5 ${SECOND_LINE}`}>
                Mode <span className="font-mono text-[var(--rt-accent)]">{mode.label}</span> ·{' '}
                {mode.freqHz.toFixed(1)} Hz · <span className="capitalize">{mode.type}</span>
                {' · '}gold speaker · blue desk
              </p>
            </div>
            <p
              className={SECOND_LINE}
              data-testid={deskIsNull ? 'null-warning' : 'desk-pressure'}
            >
              Desk |P| = {deskPressure.toFixed(2)}
              {deskIsNull ? ' · null zone' : ''}
            </p>
          </div>
          <div ref={heatmapRef}>
            {heatmapNear ? (
              <ModeHeatmapCanvas
                grid={grid}
                lengthM={lengthM}
                widthM={widthM}
                heightM={heightM}
                deskX={deskXM}
                deskY={deskYM}
                deskIsNull={deskIsNull}
                speakerX={speakerXM}
                speakerY={speakerYM}
              />
            ) : (
              <HeatmapPoster />
            )}
          </div>
        </div>
      )}

      <div className="space-y-3">
        <div>
          <h3 className={CONTENT_NAME}>Broadband seat response</h3>
          <p className={`mt-0.5 ${SECOND_LINE}`}>
            All modes ≤250 Hz at the desk · relative dB · ticks = resonances (axial stronger)
          </p>
        </div>
        <ToolFreqChart
          freqs={freqs}
          curves={[
            { mag: magPure, tone: 'reference', label: 'Pure' },
            { mag: seat.mag, tone: 'accent', label: 'At seat' },
          ]}
          relative
          dbRange={{ min: -24, max: 18 }}
          modeTicks={modeTicks}
          markerHz={mode?.freqHz}
          markerLabel={mode ? `${mode.freqHz.toFixed(0)} Hz` : undefined}
          fMax={250}
        />
        <ToolMultiAuralization
          states={states}
          stateButtons={[
            { id: 'pure', label: 'Pure' },
            { id: 'seat', label: 'At seat' },
          ]}
          defaultState="seat"
        />
      </div>

      <div className="overflow-x-auto max-h-48 overflow-y-auto rounded-lg border border-[var(--app-border)]">
        <table className={`w-full ${CONTENT}`} data-testid="mode-table">
          <thead className="sticky top-0 bg-[var(--app-card)] text-[var(--rt-ink-dim)]">
            <tr>
              <th className="text-left p-2">Mode</th>
              <th className="text-left p-2">Hz</th>
              <th className="text-left p-2">Type</th>
            </tr>
          </thead>
          <tbody>
            {modes.slice(0, 40).map((m) => {
              const selected = mode && m.label === mode.label && m.freqHz === mode.freqHz;
              return (
                <tr
                  key={m.label + m.freqHz}
                  tabIndex={0}
                  onClick={() => setTargetHz(m.freqHz)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setTargetHz(m.freqHz);
                    }
                  }}
                  className={
                    'cursor-pointer transition-colors ' +
                    (selected
                      ? 'bg-[color-mix(in_oklch,var(--rt-accent)_15%,transparent)] text-[var(--rt-ink)]'
                      : 'text-[var(--rt-ink-dim)] hover:bg-[var(--app-border)]/40')
                  }
                >
                  <td className="p-2 font-mono">{m.label}</td>
                  <td className="p-2 font-mono">{m.freqHz.toFixed(1)}</td>
                  <td className="p-2 capitalize">{m.type}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ToolLeadIn result={result} />
    </div>
  );
}
